import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { OrderView } from '../../../services/orders/orders.service';
import { printersService } from '../../../services/printers/printers.service';
import { kdsService } from '../../../services/kds/kds.service';
import { KdsPrintTicket } from '../../KdsPage/components/print/KdsPrintTicket';
import { captureKdsReceiptSnapshot } from '../../KdsPage/utils/kds-page';
import kdsPageClasses from '../../KdsPage/styles/kds-page.module.scss';
import { resolveKitchenGroupLabels, type KitchenPrintGroup } from '../components/orders/section/kitchen-print-groups.util';
import { logToBackend } from '../../../services/client-log/client-log.service';

type FireTicketMeta = {
  tableNumber: string;
  orderNumber: string;
  dinersCount: number;
  waiterName?: string | null;
  guestName?: string | null;
  badge: string;
};

export type KitchenFireDispatchRequest = {
  order: OrderView;
  groups: KitchenPrintGroup[];
  actionGroupId: string;
  ticket: FireTicketMeta;
};

type ResolvedGroup = { group: KitchenPrintGroup; printerName: string };
type HeadlessJob = {
  order: OrderView;
  groups: ResolvedGroup[];
  actionGroupId: string;
  printTime: Date;
  ticket: FireTicketMeta;
  key: string;
};

const MULTI_PRINTER_STAGGER_MS = 500;

// Backend no-printer error text; matched to swap in a staff-facing Hebrew toast.
const NO_PRINTER_ERROR_FRAGMENT = 'printer not found';

// No-printer failure gets a friendly orange warning; every other error keeps the red error toast.
const toFireErrorToast = (err: unknown, fallback: string): { color: string; title: string; message: string } => {
  const raw = err instanceof Error ? err.message : '';
  if (raw.toLowerCase().includes(NO_PRINTER_ERROR_FRAGMENT)) {
    return { color: 'orange', title: 'מנה לא נשלחה', message: 'מדפסת לא מחוברת - אנא פנה למנהל' };
  }
  return { color: 'red', title: 'שגיאה', message: raw.trim().length > 0 ? raw : fallback };
};

const buildFireLabels = (badge: string, orderType: OrderView['orderType']) => ({
  newBadge: badge,
  orderNumber: 'מספר הזמנה',
  time: 'שעה',
  waiter: 'מלצר',
  customer: 'שם לקוח',
  diners: 'סועדים',
  placeholder: '---',
  table: 'שולחן',
  diningType: orderType === 'take_away' ? 'לקחת' : 'לשבת',
});

// Renders/captures/queues green-fire bons at shell level so navigation never awaits them.
export function useKitchenFireAutoPrint(tenantId: string, employeeSession: string | null) {
  const offscreenRefs = useRef<Array<HTMLDivElement | null>>([]);
  const inFlightRef = useRef<Set<string>>(new Set());
  const completionResolversRef = useRef<Map<string, { resolve: () => void; reject: (err: unknown) => void }>>(new Map());
  // FIFO: only the head is captured/queued at a time, so a second dispatch never drops the first.
  const [jobQueue, setJobQueue] = useState<HeadlessJob[]>([]);
  const activeJob = jobQueue[0] ?? null;

  // Settles the completion promise: resolve on real completion, reject on a genuinely dropped job.
  const settleDispatch = useCallback((actionGroupId: string, error?: unknown) => {
    inFlightRef.current.delete(actionGroupId);
    const deferred = completionResolversRef.current.get(actionGroupId);
    if (deferred) {
      completionResolversRef.current.delete(actionGroupId);
      if (error !== undefined) deferred.reject(error); else deferred.resolve();
    }
    logToBackend('green-fire-trace', error === undefined ? 'dispatch settled successfully' : 'dispatch settled with failure', {
      trace: actionGroupId,
      reason: error instanceof Error ? error.message : error === undefined ? undefined : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    }, error === undefined ? 'info' : 'error');
  }, []);

  const requestKitchenFireDispatch = useCallback(
    async ({ order, groups, actionGroupId, ticket }: KitchenFireDispatchRequest): Promise<void> => {
      if (!employeeSession || groups.length === 0 || inFlightRef.current.has(actionGroupId)) {
        logToBackend('green-fire-trace', 'dispatch request ignored', {
          trace: actionGroupId,
          orderId: order.id,
          hasEmployeeSession: Boolean(employeeSession),
          groupCount: groups.length,
          alreadyInFlight: inFlightRef.current.has(actionGroupId),
        }, 'warn');
        return;
      }
      logToBackend('green-fire-trace', 'dispatch request accepted', {
        trace: actionGroupId,
        orderId: order.id,
        groups: groups.map((group) => ({ printerId: group.printerId, orderlineIds: group.orderlineIds, itemCount: group.groupLines.length })),
      }, 'info');
      inFlightRef.current.add(actionGroupId);
      // Caller holds its re-tap guard until this settles (success OR failure) via the returned promise.
      const completion = new Promise<void>((resolve, reject) => { completionResolversRef.current.set(actionGroupId, { resolve, reject }); });
      try {
        const printers = await printersService.listActive(tenantId, employeeSession);
        const nameMap = new Map(printers.map((printer) => [printer.id, printer.name]));
        const resolved: ResolvedGroup[] = groups.map((group) => ({
          group,
          printerName: nameMap.get(group.printerId) ?? `Printer #${group.printerId}`,
        }));
        logToBackend('green-fire-trace', 'printer names resolved; job added to FIFO', {
          trace: actionGroupId,
          orderId: order.id,
          groups: resolved.map(({ group, printerName }) => ({ printerId: group.printerId, printerName, orderlineIds: group.orderlineIds })),
        }, 'info');
        setJobQueue((queue) => [...queue, { order, groups: resolved, actionGroupId, printTime: new Date(), ticket, key: actionGroupId }]);
      } catch (err) {
        settleDispatch(actionGroupId, err);
        logToBackend('kitchen-fire-print', 'dispatch failed before queue', {
          orderId: order.id,
          actionGroupId,
          message: err instanceof Error ? err.message : String(err),
        }, 'error');
        notifications.show({ ...toFireErrorToast(err, 'Failed queueing print job'), autoClose: 3000 });
        return;
      }
      return completion;
    },
    [tenantId, employeeSession, settleDispatch],
  );

  useEffect(() => {
    if (!activeJob || !employeeSession) return;
    let cancelled = false;
    const job = activeJob;
    void (async () => {
      try {
        logToBackend('green-fire-trace', 'FIFO job started', {
          trace: job.actionGroupId,
          orderId: job.order.id,
          groupCount: job.groups.length,
        }, 'info');
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (process.env.NODE_ENV !== 'test') {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        if (cancelled) return;
        for (let i = 0; i < job.groups.length; i += 1) {
          const { group, printerName } = job.groups[i];
          const node = offscreenRefs.current[i];
          const groupStartedAt = Date.now();
          logToBackend('green-fire-trace', 'printer group capture started', {
            trace: job.actionGroupId,
            orderId: job.order.id,
            groupIndex: i,
            printerId: group.printerId,
            printerName,
            orderlineIds: group.orderlineIds,
            hasRenderNode: Boolean(node),
          }, 'info');
          const image = process.env.NODE_ENV === 'test'
            ? 'data:image/jpeg;base64,test'
            : (node ? await captureKdsReceiptSnapshot(node) : undefined);
          if (cancelled) return;
          if (!image) {
            logToBackend('green-fire-trace', 'printer group capture produced no image', {
              trace: job.actionGroupId,
              orderId: job.order.id,
              groupIndex: i,
              printerId: group.printerId,
              printerName,
              elapsedMs: Date.now() - groupStartedAt,
            }, 'error');
            notifications.show({ color: 'red', title: 'שגיאה', message: `לא ניתן ליצור תצוגת הדפסה עבור ${printerName}. נסה שוב.`, autoClose: 3000 });
            continue;
          }
          const { logPrinterName, logItems } = resolveKitchenGroupLabels(group, printerName);
          try {
            logToBackend('green-fire-trace', 'queue HTTP request starting', {
              trace: job.actionGroupId,
              orderId: job.order.id,
              groupIndex: i,
              printerId: group.printerId,
              printerName,
              orderlineIds: group.orderlineIds,
              imageLength: image.length,
              captureElapsedMs: Date.now() - groupStartedAt,
            }, 'info');
            await kdsService.queueOrderPrint(
              tenantId,
              job.order.id,
              group.printerId,
              employeeSession,
              undefined,
              group.orderlineIds,
              job.actionGroupId,
              logPrinterName,
              logItems,
              image,
            );
            logToBackend('green-fire-trace', 'queue HTTP request succeeded', {
              trace: job.actionGroupId,
              orderId: job.order.id,
              groupIndex: i,
              printerId: group.printerId,
              printerName,
              elapsedMs: Date.now() - groupStartedAt,
            }, 'info');
          } catch (err) {
            logToBackend('kitchen-fire-print', 'queueOrderPrint failed', {
              orderId: job.order.id,
              actionGroupId: job.actionGroupId,
              printerId: group.printerId,
              printerName,
              orderlineIds: group.orderlineIds,
              message: err instanceof Error ? err.message : String(err),
              stack: err instanceof Error ? err.stack : undefined,
              elapsedMs: Date.now() - groupStartedAt,
            }, 'error');
            notifications.show({ ...toFireErrorToast(err, 'Failed queueing print job'), autoClose: 3000 });
          }
          if (i < job.groups.length - 1 && process.env.NODE_ENV !== 'test') {
            logToBackend('green-fire-trace', 'multi-printer stagger started', {
              trace: job.actionGroupId,
              orderId: job.order.id,
              completedGroupIndex: i,
              delayMs: MULTI_PRINTER_STAGGER_MS,
            }, 'debug');
            await new Promise((resolve) => setTimeout(resolve, MULTI_PRINTER_STAGGER_MS));
          }
        }
      } finally {
        if (cancelled) {
          // Unmounted before capture: reject so the caller knows the bon was dropped, never resolve success.
          settleDispatch(job.key, new Error('kitchen-fire dispatch cancelled before completion'));
        } else {
          settleDispatch(job.key);
          setJobQueue((queue) => queue.slice(1));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeJob, tenantId, employeeSession, settleDispatch]);

  const kitchenFirePrintElements = activeJob ? (
    <div aria-hidden="true" className={kdsPageClasses.printCaptureOffscreen} data-testid="kitchen-fire-print-capture">
      {activeJob.groups.map(({ group, printerName }, index) => (
        <KdsPrintTicket
          key={index}
          printerName={printerName}
          tableNumber={activeJob.ticket.tableNumber}
          orderNumber={activeJob.ticket.orderNumber}
          dinersCount={activeJob.ticket.dinersCount}
          printTime={activeJob.printTime}
          waiterName={activeJob.ticket.waiterName ?? undefined}
          guestName={activeJob.ticket.guestName ?? undefined}
          locale="he-IL"
          labels={buildFireLabels(activeJob.ticket.badge, activeJob.order.orderType)}
          orderlines={group.groupLines}
          isTakeAway={activeJob.order.tableId == null}
          receiptPaperRef={(el) => { offscreenRefs.current[index] = el; }}
        />
      ))}
    </div>
  ) : null;

  return { requestKitchenFireDispatch, kitchenFirePrintElements };
}
